import React from "react";

import cx from "classnames";
import moment from "moment";

import {EventOrigin, recordEvent} from "@globals/config/eventUtils";
import {SECONDARY_ERROR_MODAL_CONTENT} from "@globals/constants/error_modal";
import useCutoffTimes from "@globals/hooks/useCutoffTimes";
import useScript from "@globals/hooks/useScript";
import {ExtendedError} from "@globals/types/ExtendedError";

import ApiErrorMessage from "@components/ApiErrorMessage";
import Button, {ButtonAccent} from "@components/Button";
import {DishDetailsConfigProvider} from "@components/DishDetails";
import ImageBanner, {ImageBannerName} from "@components/ImageBanner";
import Menu from "@components/Menu";
import {MenuType} from "@components/Menu/interfaces";
import Welcome from "@components/Welcome";
import DayPartSelect from "@components/forms/inputs/DayPartSelect";
import LocationSelect from "@components/forms/inputs/LocationSelect";
import SortingSelect from "@components/forms/inputs/SortingSelect";
import Footer from "@components/layout/Footer";
import Header from "@components/layout/Header";
import {AppRoutes} from "@components/layout/Header/interfaces";
import MaxWidthContainer from "@components/layout/MaxWidthContainer";
import PageContainer from "@components/layout/PageContainer";
import SubHeader from "@components/layout/SubHeader";
import CutoffModal from "@components/modals/CutoffModal";
import ErrorModal from "@components/modals/ErrorModal";

import DailyCart from "../../components/DailyCart";
import DailyDateSelector from "./DailyDateSelector/DailyDateSelector";
import DailyDishReview from "./DailyDishReview/DailyDishReview";
import OrderCutoffTimeInfo from "./OrderCutoffTimeInfo/OrderCutoffTimeInfo";
import useDailyMenu from "./hooks/useDailyMenu";
import useDailyOrders from "./hooks/useDailyOrders";
import useDaySubsidyInfo from "./hooks/useDaySubsidyInfo";
import styles from "./index.scss";
import DateChangeModal from "./modals/DateChangeModal";

export default function DailyMenuApp(): JSX.Element {
  useScript("https://disutgh7q0ncc.cloudfront.net/beacon.js");
  const {
    selectedLocationId,
    hasNoLunchAccess,
    preSelectedItem,
    sortOption,
    setSortOption,
    menu,
    hasNoLunchProduct,
    latestError: menuError,
    menuParams,
    menuDates,
    selectedDate,
    closedDates,
    holidayDetails,
    nextMenuDate,
    goToNextMenuDate,
    isApiError,
    isDateAdjusted,
    clearDayPartSearchParam,
    warning,
    clearWarning,
  } = useDailyMenu();

  const {
    addItem,
    partialOrders,
    handleItemUpdate,
    isLoading: isOrdersLoading,
    checkout,
    latestError: orderError,
    resetOrder,
  } = useDailyOrders();

  const showContent = !hasNoLunchAccess && !isApiError;

  const {daySubsidyInfo} = useDaySubsidyInfo(selectedDate);
  const isCurrentDayClosed =
    (selectedDate && closedDates?.includes(selectedDate)) ?? false;

  const replaceMenuDate = () => goToNextMenuDate(true);

  const {cutoffTimes} = useCutoffTimes({
    selectedDate: isDateAdjusted ? undefined : selectedDate,
    selectedLocation: selectedLocationId,
  });

  const recordLocationChange = () => {
    // User manually changes location – the day part in the url is no longer relevant
    clearDayPartSearchParam();
    recordEvent(EventOrigin.PAGE, "change location", true);
  };

  const handleSortingChange = (value: number) => {
    setSortOption(value);
    recordEvent(
      EventOrigin.PAGE,
      "sort dishes",
      menu?.sort_options[value].name
    );
  };

  let errorOrWarning: ExtendedError | undefined;
  if (orderError) {
    errorOrWarning = orderError;
  } else if (warning) {
    errorOrWarning = {
      title: "Oops!",
      status: undefined,
      message: warning,
      addSupportMessage: false,
    } as ExtendedError;
  }

  const dailyCartComponent = (
    <DailyCart
      resetOrder={resetOrder}
      partialOrders={partialOrders}
      handleItemUpdate={handleItemUpdate}
      isLoading={isOrdersLoading}
      goToNextMenuDate={goToNextMenuDate}
      selectedDate={selectedDate}
      nextMenuDate={nextMenuDate}
      checkout={checkout}
    />
  );

  return (
    <DishDetailsConfigProvider
      showFavorites={true}
      showFavoriteHoverBox={true}
      showReviews={true}
      menuType={MenuType.Individual}
      preSelectedItem={preSelectedItem}
      changeItemQty={addItem}
      addButtonText={"Add"}
      showPriceInPurchaseButton={true}
      hidePriceInDishDetails={true}
      selectedDate={selectedDate}
      hasDateSelector={true}
    >
      <Welcome />
      <CutoffModal
        cutoffTimes={cutoffTimes}
        confirmButtonText="See next menu"
        onConfirm={replaceMenuDate}
        onClose={replaceMenuDate}
        showOnLoad={true}
      />
      <ErrorModal
        error={errorOrWarning}
        defaultContent={SECONDARY_ERROR_MODAL_CONTENT}
        onClose={clearWarning}
      />
      <DateChangeModal isDateAdjusted={isDateAdjusted} />
      <PageContainer withHeader={true}>
        <Header
          showMealPrograms={false}
          selectedHeaderLink={AppRoutes.Menu}
          daySubsidyInfo={isCurrentDayClosed ? undefined : daySubsidyInfo}
        />
        <MaxWidthContainer className={styles.container} tagName="main">
          {isApiError && <ApiErrorMessage />}
          {hasNoLunchAccess && (
            <div className={styles.noLunchError}>
              <p>
                Please contact a specialist to set up a program for this
                account.
              </p>
            </div>
          )}
          {showContent && (
            <section className={styles.grid}>
              <nav className={styles.datesWrapper} aria-label="Menu date">
                <DailyDateSelector
                  selectedDate={selectedDate}
                  selectedLocationId={selectedLocationId}
                  menuDates={menuDates}
                  closedDates={closedDates}
                  holidayDetails={holidayDetails}
                />
                <div className={styles.cartInDatesWrapper}>
                  {dailyCartComponent}
                </div>
              </nav>
              <nav className={styles.filtersWrapper} aria-label="Page">
                <SubHeader className={styles.filtersContainer}>
                  <div className={styles.selectorsWrapper}>
                    <LocationSelect
                      label="Location"
                      value={selectedLocationId}
                      name="location"
                      className={cx(styles.select, styles.locationSelect)}
                      shouldChangeUserLocation={true}
                      onChange={recordLocationChange}
                      showOneDayPart={true}
                      isTaller
                    />
                    <DayPartSelect
                      value={selectedLocationId}
                      name={"day_part"}
                      className={cx(styles.select, styles.dayPartSelect)}
                      shouldChangeUserLocation={true}
                      onChange={recordLocationChange}
                      isTaller
                    />
                    <SortingSelect
                      label="Sort dishes"
                      name="dish_sort"
                      sortOptions={menu?.sort_options}
                      className={cx(styles.select, styles.sortingSelect)}
                      value={sortOption}
                      onChange={handleSortingChange}
                      mobileViewIcon="nomicon-sort"
                      isTaller
                    />
                  </div>
                  <div className={styles.cartCutoffWrapper}>
                    <OrderCutoffTimeInfo cutoffTimes={cutoffTimes} />
                    {dailyCartComponent}
                  </div>
                </SubHeader>
              </nav>
              {hasNoLunchProduct ? (
                <div className={cx(styles.noLunchError, styles.noMealProgram)}>
                  No meal program is active for this delivery location. Please
                  select a different address from the location dropdown above or
                  contact a specialist to set up a meal program for this
                  location.
                </div>
              ) : (
                <div className={styles.menuWrapper}>
                  <DailyDishReview />
                  {selectedDate && (
                    <ImageBanner
                      name={ImageBannerName.DailyMenuAlert}
                      locationId={selectedLocationId}
                      date={selectedDate}
                      containerClassName={styles.imageBannerContainer}
                    />
                  )}
                  <Menu
                    {...menuParams}
                    preSelectedItemSlug={preSelectedItem?.slug}
                    selectedSortOption={sortOption}
                    isHidden={Boolean(menuError)}
                    hiddenMessage={menuError}
                    headerContent={
                      selectedDate && (
                        <ImageBanner
                          name={ImageBannerName.DailyMenuPromoBanner}
                          date={selectedDate}
                          locationId={selectedLocationId}
                          containerClassName={cx(
                            styles.imageBannerContainer,
                            styles.promoBannerContainer
                          )}
                        />
                      )
                    }
                    footerContent={
                      nextMenuDate && (
                        <div className={styles.nextMenuLinkContainer}>
                          <Button
                            onClick={() => goToNextMenuDate()}
                            accent={ButtonAccent.AccentGrey}
                            className={styles.nextMenuLink}
                            withBorder
                          >
                            See{" "}
                            {moment(nextMenuDate, "YYYY-MM-DD").format("dddd")}
                            's Menu
                          </Button>
                        </div>
                      )
                    }
                  />
                </div>
              )}
            </section>
          )}
        </MaxWidthContainer>
      </PageContainer>
      <Footer />
    </DishDetailsConfigProvider>
  );
}
